var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { relativeUrl } from '../Url';
import createSlidingKeyValueCache from '../sliding-key-value-cache';
import { getFastCache } from '../DynamicData';
import { isBrowser } from '../device-type';
const cache = createSlidingKeyValueCache('currentpage', 50, 10, (item, url) => (item.url === url ? item : findIn(item, url)), item => item.shouldCache, item => item.shouldCache, item => item.shouldCache, item => item.url);
let cacheUrlMatcher = url => relativeUrl(url, false);
/**
 * By default we will look for items in the cache where the `url` property exactly
 * matches the current url, and that works for most cases. But if you list products
 * on the PLP but have variation urls you can override the default behavior.
 *
 * Let's say that the product card data on the PLP looks like this:
 * {
 *   url: '/myproduct',
 *   code: 'P1',
 *   componentName: '...',
 *   variants: [
 *     {code: 'V1', url: '/myproduct?variant=V1'}
 *     {code: 'V2', url: '/myproduct?variant=V2'}
 *   ]
 * }
 *
 * And you want the link for the card to be one of the variant urls. In that
 * case we won't ever hit the partial cache by default, because we'll try to match
 * the root url (`/myproduct`) with the current url (`/myproduct?variant=V1`).
 *
 * In this case you can set your own matcher that does something like this:
 * setCacheUrlMatcher(url => {
 *   const parts = relativeUrl(url).split('?');
 *   return const[0];
 * });
 *
 * Which removes the query string and will look in the cache for `/myproduct` instead of
 * `/myproduct?variant=V1`
 */
export function setCacheUrlMatcher(matcher) {
    cacheUrlMatcher = matcher;
}
export function isCacheItem(item) {
    return !!(item && item.url && item.$cache && item.componentName && item.meta);
}
export function getCacheItem(url) {
    return __awaiter(this, void 0, void 0, function* () {
        const key = cacheUrlMatcher(url);
        let cacheItem = yield cache.getCacheItem(key);
        if (cacheItem === null) {
            const dynamicDataFastCache = getFastCache();
            cacheItem = findIn(dynamicDataFastCache, key);
        }
        return cacheItem;
    });
}
export function setCacheItem(item, fastCacheOnly = false) {
    return cache.setCacheItem(item, fastCacheOnly);
}
export function clearCacheItem(url) {
    const key = cacheUrlMatcher(url);
    return cache.clearCacheItem(key);
}
export function nukeCache() {
    return cache.nukeCache();
}
// This is used in _Splashscreen.html
if (isBrowser()) {
    window.nukeItemCache = nukeCache;
}
function findIn(cacheItem, url) {
    let partialCacheOrNull = null;
    if (Array.isArray(cacheItem)) {
        for (const item of cacheItem) {
            if (isCacheItem(item) && url === item.url) {
                if (item.$cache === 'full') {
                    return item;
                }
                else {
                    partialCacheOrNull = item;
                }
            }
            // A partial never contains cache items
            if (!isCacheItem(item) || item.$cache !== 'partial') {
                const deep = findIn(item, url);
                if (deep) {
                    return deep;
                }
            }
        }
    }
    else if (cacheItem instanceof Object) {
        if (isCacheItem(cacheItem) && url === cacheItem.url) {
            if (cacheItem.$cache === 'full') {
                return cacheItem;
            }
            else {
                partialCacheOrNull = cacheItem;
            }
        }
        // A partial never contains cache items
        if (!isCacheItem(cacheItem) || cacheItem.$cache !== 'partial') {
            const keys = Object.keys(cacheItem);
            for (const key of keys) {
                const item = cacheItem[key];
                if (isCacheItem(item) && url === item.url) {
                    if (item.$cache === 'full') {
                        return item;
                    }
                    else {
                        partialCacheOrNull = item;
                    }
                }
                const deep = findIn(item, url);
                if (deep) {
                    return deep;
                }
            }
        }
    }
    return partialCacheOrNull;
}
